<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="ds-view-page-title" class="mb-3">
              {{ dataSubject.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('data_subject')">
                <GuideButton :guide="getGuide('data_subject')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-ds-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                <span v-if="isGranted([permissions.DATA_SUBJECT_EDIT, permissions.DATA_SUBJECT_EDIT_OWN], dataSubject.created_by) && (!getClient.parent || (getClient.parent && dataSubject.partition))"
                      class="btn btn-edit"
                      qid="edit-ds-button"
                      @click="showEditDSModal(dataSubject)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.DATA_SUBJECT_DELETE, permissions.DATA_SUBJECT_DELETE_OWN], dataSubject.created_by) && (!getClient.parent || (getClient.parent && dataSubject.partition))"
                      class="btn btn-delete"
                      qid="delete-ds-button"
                      @click="showConfirmDeleteModal(dataSubject)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
                <span class="ml-2 text-muted small" qid="view-ds-modified-date">
                {{ dataSubject.updated_at | formatDate }}
              </span>
              <Avatar v-if="dataSubject.updated_by" :object="dataSubject.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
              <span v-if="dataSubject.updated_by" qid="view-ds-modified-by">
                {{ dataSubject.updated_by.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="content-view-body">
          <br>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.data_subjects.create.description_label') }}
            </p>
            <hr>
            <p qid="ds-description" class="text-formatted">{{ dataSubject.description }}</p>
            <br>
          </div>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.data_subjects.create.subjects_label') }}
            </p>
            <hr>
            <p qid="ds-number-of-subjects">{{ dataSubject.number_of_subjects }}</p>
            <br>
          </div>
        </div>
      </div>
    </div>


    <!-- Edit data subject modal template -->
    <b-modal ref="edit-data-subjects" hide-footer @hide="hideEditDSModal">
      <div class="d-block text-center">
        <h4 qid="update-ds-title">
          {{ $t('maintenance.data_subjects.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ds-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editDSFormSubmit" qid="create-ds-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-ds-name-label">
              {{ $t('maintenance.data_subjects.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-ds-form-name"
              type="text"
              v-model="dsForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.data_subjects.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ds-description-label">
              {{ $t('maintenance.data_subjects.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-ds-form-description"
              v-model="dsForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.data_subjects.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': nosInput.length}">
            <label qid="edit-ds-subjects-label">
              {{ $t('maintenance.data_subjects.create.subjects_label') }}
            </label>
            <multiselect
                v-model="dsForm.number_of_subjects"
                :options="nosItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.data_subjects.create.subjects_placeholder')"
                :preserveSearch="true"
                @search-change="handleNosInput"
                track-by="number_of_subjects"
                label="number_of_subjects"
                qid="edit-ds-form-subjects"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.data_subjects.create.subjects_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditDSModal"
                    qid="edit-ds-cancel-button"
          >
            {{ $t('maintenance.data_subjects.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-ds-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.data_subjects.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete data subject confirm modal template -->
    <b-modal ref="delete-data-subjects" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ds-title">
          {{ $t('maintenance.data_subjects.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.data_subjects.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.data_subjects.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-ds-cancel-button"
        >
          {{ $t('maintenance.data_subjects.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteDS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ds-submit-button"
                  v-if="isGranted([permissions.DATA_SUBJECT_DELETE, permissions.DATA_SUBJECT_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.data_subjects.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'DataSubjectView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.data_subjects.title'),
          to: '/maintenance/data-subjects'
        }
      ],
      dataSubject: {},
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      itemToDelete: {},
      dsForm: {
        name: '',
        description: '',
        number_of_subjects: [],
        client:'',
      },
      error: '',
      nosItems: [],
      nosInput: '',
      buttonDisabled: false,
      myGuides: [],
    }
  },
  computed: {
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadSubjects();
        this.loadContent();
      }
    }
  },
  components: {
    Avatar,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/data-subjects')
    },
    handleNosInput(input) {
      this.nosInput = input;
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('data-subjects/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.dataSubject = response.data;
        // Push data subject name in breadcrumb array
        self.breadcrumbItems.push({text: self.dataSubject.name, active: true});
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-data-subjects'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-data-subjects'].hide()
    },
    confirmDeleteDS(object) {
      let self = this;
      piincHttp.delete('data-subjects/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.data_subjects.delete.success'))
      }, function() {});
    },
    showEditDSModal(object) {
      this.error = '';
      let self = this;

      this.dsForm.slug = object.slug;
      this.dsForm.name = object.name;
      this.dsForm.description = object.description;

      // Preselect number of subjects on edit modal
      if (object.number_of_subjects) {
        this.dsForm.number_of_subjects = []
        _.find(this.nosItems, function(item) {
          if (item.number_of_subjects == object.number_of_subjects) {
            self.dsForm.number_of_subjects = item;
          }
        });
      }

      this.$refs['edit-data-subjects'].show()
    },
    hideEditDSModal() {
      this.loadSubjects();
      this.nosInput = ''
      this.$refs['edit-data-subjects'].hide()
    },
    editDSFormSubmit(evt) {
      this.buttonDisabled = true;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.dsForm.client = this.getClient.slug;

      if (this.nosInput && this.nosInput.length) {
        this.dsForm.number_of_subjects = this.nosInput
      } else {
        if (this.dsForm.number_of_subjects && this.dsForm.number_of_subjects.number_of_subjects) {
          this.dsForm.number_of_subjects = this.dsForm.number_of_subjects.number_of_subjects
        } else {
          this.dsForm.number_of_subjects = '';
        }
      }

      piincHttp.put('data-subjects/' + this.dsForm.slug, this.dsForm).then(function(response) {
        self.hideEditDSModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.dataSubject.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.data_subjects.view', params: { slug: response.data.slug }})
        }
        self.$toastr('success', self.$t('maintenance.data_subjects.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
          if (error.data.errors.number_of_subjects) {
            self.error += ' ' + error.data.errors.number_of_subjects[0]
          }
        }
      })
    },
    loadSubjects() {
      let self = this;
      if (this.getClient && this.getClient.slug) {
        piincHttp.get('/data-subjects/subjects', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.nosItems = response.data
          if (self.nosItems && !self.nosItems.length) {
            self.nosItems.push({number_of_subjects: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
    }
    this.loadSubjects();
    this.loadGuides('data_subject');
  }
}
</script>
